// The number pad's rules, kept pure so they are tested on their own. A key either changes the entry or is refused
// (the display nudges), so a player can never type a value the question would not accept: no more decimals than
// the question asks for, no more whole digits than the bounds allow, never above the maximum. A value below the
// minimum can be on its way (0 before 0.9), so it only stops the submit.
import type { NumberSpec } from '@shared/types';

export interface KeypadSpec { decimals: number; min: number; max: number }
export type KeypadKey = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '.' | '-' | 'del' | 'clear';
export interface KeyResult { text: string; refused: boolean }
export type EntryProblem = 'empty' | 'low' | 'high';

const EPS = 1e-9;
const DIGITS = new Set(['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']);

/** The pad's settings from the question, with safe defaults for anything missing or odd. */
export function keypadSpec(spec?: Partial<Pick<NumberSpec, 'decimals' | 'min' | 'max'>>): KeypadSpec {
  const decimals = Math.min(4, Math.max(0, Math.floor(Number(spec?.decimals) || 0)));
  let min = Number.isFinite(spec?.min) ? Number(spec!.min) : 0;
  let max = Number.isFinite(spec?.max) ? Number(spec!.max) : 999;
  if (max < min) [min, max] = [max, min];
  if (max === min) max = min + 999;
  return { decimals, min, max };
}

export const hasDecimalKey = (s: KeypadSpec) => s.decimals > 0;
export const hasSignKey = (s: KeypadSpec) => s.min < 0;
const wholeDigits = (s: KeypadSpec) => String(Math.floor(Math.max(Math.abs(s.min), Math.abs(s.max)))).length;

/** The value an entry stands for; undefined while it is empty or only a sign or a point. */
export function entryValue(text: string): number | undefined {
  if (!/^-?(\d+\.?\d*|\.\d+)$/.test(text)) return undefined;
  const v = Number(text);
  return Number.isFinite(v) ? v : undefined;
}

const tooHigh = (v: number, s: KeypadSpec) => v > s.max + EPS;
const tooLow = (v: number, s: KeypadSpec) => v < s.min - EPS;

export function pressKey(text: string, key: KeypadKey, spec: KeypadSpec): KeyResult {
  const refuse = (): KeyResult => ({ text, refused: true });
  const ok = (t: string): KeyResult => ({ text: t, refused: false });
  if (key === 'clear') return ok('');
  if (key === 'del') return ok(text.slice(0, -1) === '-' ? '' : text.slice(0, -1));
  if (key === '.') {
    if (!hasDecimalKey(spec) || text.includes('.')) return refuse();
    return ok(text === '' ? '0.' : text === '-' ? '-0.' : `${text}.`);
  }
  if (key === '-') {
    if (!hasSignKey(spec)) return refuse();
    const next = text.startsWith('-') ? text.slice(1) : `-${text}`;
    const v = entryValue(next);
    if (v !== undefined && (tooHigh(v, spec) || tooLow(v, spec))) return refuse();
    return ok(next);
  }
  if (!DIGITS.has(key)) return refuse();
  // No leading zeros: a digit typed after a lone 0 replaces it.
  const base = text === '0' ? '' : text === '-0' ? '-' : text;
  const dot = base.indexOf('.');
  if (dot >= 0) {
    if (base.length - dot - 1 >= spec.decimals) return refuse();
  } else if (base.replace('-', '').length >= wholeDigits(spec)) {
    return refuse();
  }
  const next = `${base}${key}`;
  const v = entryValue(next);
  if (v !== undefined && (tooHigh(v, spec) || (spec.min < 0 && tooLow(v, spec)))) return refuse();
  return ok(next);
}

/** Why an entry cannot be sent yet, if it cannot. */
export function entryProblem(text: string, spec: KeypadSpec): EntryProblem | undefined {
  const v = entryValue(text);
  if (v === undefined) return 'empty';
  if (tooLow(v, spec)) return 'low';
  if (tooHigh(v, spec)) return 'high';
  return undefined;
}
export const canSubmit = (text: string, spec: KeypadSpec) => entryProblem(text, spec) === undefined;

/** A value the way the question writes it: its own number of decimals, UK style, a proper minus sign. */
export function formatNumber(v: number, decimals: number): string {
  const s = Math.abs(v).toLocaleString('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping: false });
  return v < 0 && Number(s) !== 0 ? `−${s}` : s;
}

/** The entry as it is shown while typing: a proper minus sign, nothing else changed. */
export const displayEntry = (text: string) => text.replace('-', '−');

/** Maps a physical key (desktop players) to a pad key. */
export function keyFromKeyboard(key: string): KeypadKey | undefined {
  if (DIGITS.has(key)) return key as KeypadKey;
  if (key === '.' || key === ',') return '.';
  if (key === '-' || key === '−') return '-';
  if (key === 'Backspace') return 'del';
  if (key === 'Delete' || key === 'Escape') return 'clear';
  return undefined;
}
